import { useTranslation } from 'react-i18next';
import type { NotificationItem } from '@/api/notifications/notificationsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { cn } from '@/lib/utils';
import {
  resolveNotificationCategory,
  resolveNotificationCategoryIcon,
} from './notificationPresentation';

interface NotificationRowProps {
  notification: NotificationItem;
  onOpen: () => void;
  onMarkAsRead: () => void;
  isMarkingAsRead: boolean;
}

export function NotificationRow({
  notification,
  onOpen,
  onMarkAsRead,
  isMarkingAsRead,
}: NotificationRowProps) {
  const { t } = useTranslation('notifications');
  const category = resolveNotificationCategory(notification.notificationType);
  const categoryIcon = resolveNotificationCategoryIcon(category);

  return (
    <li
      className={cn(
        'flex gap-2 px-4 py-3.5',
        notification.isRead === false ? 'bg-zoooffwhite' : ''
      )}
    >
      <button type="button" onClick={onOpen} className="flex-1 min-w-0 flex gap-3 text-left">
        <span className="mt-0.5 w-9 h-9 rounded-full bg-zoovpalelavender flex items-center justify-center text-zooviolet flex-shrink-0">
          <Icon name={categoryIcon} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            {notification.isRead === false ? (
              <span
                className="w-2 h-2 rounded-full bg-zooprimary flex-shrink-0"
                aria-hidden="true"
              />
            ) : null}
            <span className="block text-sm font-semibold text-zooindigo truncate">
              {notification.title}
            </span>
          </span>
          <span className="block text-xs text-zoomuted mt-0.5 line-clamp-2">
            {notification.body}
          </span>
          <span className="block text-[11px] text-zoomuted mt-1">
            {formatRelativeTime(notification.createdAt)}
          </span>
        </span>
      </button>
      {notification.isRead === false ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="w-8 h-8 flex-shrink-0 self-start"
          aria-label={t('row.markAsRead')}
          isLoading={isMarkingAsRead}
          onClick={onMarkAsRead}
        >
          <Icon name="Check" />
        </Button>
      ) : null}
    </li>
  );
}
